import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
  ICreateSmartCoolerProductPayload,
  IFetchSmartCoolerProductParams,
  IUpdateSmartCoolerProductPayload
} from '@/services/smartCoolerProducts';

// --- 1. Define the Error Interface to match getErrorPayload ---
export interface ApiErrorPayload {
  message: string;
  statusCode: number | string;
  messageDetails?: any[];
  errorCode?: string;
}

export interface ISmartCoolerSubApplication {
  id: string;
  name: string;
  created_on: string;
  updated_on: string;
  deleted_on: string | null;
}

export interface ISmartCoolerProductListItem {
  smartCoolerProductId: string;
  lintedSmartCoolerProductId: string;
  legacyId: string | null;
  productId: string;
  subAppId: string;
  barcodes: string[];
  productName: string | null;
  shortProductName: string;
  spec: any | null;
  brand: any | null;
  category: any | null;
  productImage: string | null;
  lastChangeDtm: string;
  createdOn: string;
  updatedOn: string;
  subapplication: ISmartCoolerSubApplication;
}

export interface ISmartCoolerPagination {
  totalRecords: number;
  currentPage: number;
  totalPages: number;
  nextPage: number | null;
  currentLimit: number;
}

export interface ISmartCoolerApiResponse {
  success: boolean;
  message: string;
  data: ISmartCoolerProductListItem[];
  pagination: ISmartCoolerPagination;
}

export interface IProductBarcode {
  barcode_id: string;
  code: string;
  custom: boolean;
  type: string | null;
  product_id: string;
  created_on: string;
  updated_on: string;
  deleted_on: string | null;
}

export interface IProductDetail {
  product_id: string;
  product_code: string;
  name: string;
  sanitized_name: string;
  description: string;
  nutritional_info: any | null;
  min_quantity: number;
  max_quantity: number;
  created_on: string;
  updated_on: string;
  deleted_on: string | null;
  barcodes: IProductBarcode[];
  images: any[];
}

export interface ISmartCoolerTransaction {
  scp_transaction_id: string;
  action: 'CREATE' | 'UPDATE' | 'DELETE' | 'RESTORE' | string;
  processed: boolean;
  status_code: number;
  meta: {
    data: any;
    error: any;
    response: {
      smartCoolerProductId?: string;
    };
    updated_by: string;
  };
  scp_id: string;
  sub_app_id: string | null;
  created_on: string;
  deleted_on: string | null;
}

export interface ISmartCoolerProductDetailData {
  smartCoolerProductId: string;
  lintedSmartCoolerProductId: string;
  legacyId: string | null;
  productId: string;
  subAppId: string;
  barcodes: string[];
  productName: string | null;
  shortProductName: string;
  spec: any | null;
  brand: any | null;
  category: any | null;
  productImage: string | null;
  lastChangeDtm: string;
  createdOn: string;
  updatedOn: string;
  product: IProductDetail;
  transactions: ISmartCoolerTransaction[];
}

export interface ISmartCoolerMasterProductResponse {
  success: boolean;
  message: string;
  data: ISmartCoolerProductDetailData;
}

export interface SmartCoolerProductsState {
  smartCoolerProducts: ISmartCoolerApiResponse;
  smartCoolerProduct: ISmartCoolerMasterProductResponse;
  loading: boolean;
  loadingProducts: boolean;
  loadingProductById: boolean;
  error: boolean;
  message: string;
  shouldRefetchSmartCoolerProducts: boolean;
}

export const initialState: SmartCoolerProductsState = {
  loadingProducts: false,
  loadingProductById: false,
  shouldRefetchSmartCoolerProducts: false,
  loading: false,
  error: false,
  message: '',
  smartCoolerProducts: {
    success: false,
    message: '',
    data: [],
    pagination: {
      totalRecords: 0,
      currentPage: 0,
      totalPages: 0,
      currentLimit: 0,
      nextPage: null, 
    },
  },
  smartCoolerProduct: {
    success: false,
    message: '',
    data: {
      smartCoolerProductId: '',
      lintedSmartCoolerProductId: '',
      legacyId: null,
      productId: '',
      subAppId: '',
      barcodes: [],
      productName: null,
      shortProductName: '',
      productImage: null,
      lastChangeDtm: '',
      createdOn: '',
      updatedOn: '',
      spec: null,
      brand: null,
      category: null,
      product: {
        product_id: '',
        product_code: '',
        name: '',
        sanitized_name: '',
        description: '',
        nutritional_info: null,
        min_quantity: 0,
        max_quantity: 0,
        created_on: '',
        updated_on: '',
        deleted_on: null,
        barcodes: [],
        images: [],
      },
      transactions: [],
    },
  },
};

const smartCoolerProductsSlice = createSlice({
  name: 'smartCoolerProducts',
  initialState,
  reducers: {
    fetchSmartCoolerProducts: (state, _action: PayloadAction<IFetchSmartCoolerProductParams>) => {
      state.loadingProducts = true;
      state.error = false;
      state.message = '';
      state.shouldRefetchSmartCoolerProducts = false;
    },
    fetchSmartCoolerProductsSuccess: (state, action: PayloadAction<ISmartCoolerApiResponse>) => {
      state.loadingProducts = false;
      state.smartCoolerProducts = action.payload;
      state.error = false;
    },
    // --- UPDATED: Changed string to ApiErrorPayload | any ---
    fetchSmartCoolerProductsFailure: (state, action: PayloadAction<ApiErrorPayload | any>) => {
      state.loadingProducts = false;
      state.error = true;
      state.message = action.payload?.message || 'Failed to fetch Smart Cooler Products';
    },

    fetchSmartCoolerProductById: (state, action: PayloadAction<string>) => {
      state.loadingProductById = true;
      state.error = false;
      state.message = '';
    },
    fetchSmartCoolerProductByIdSuccess: (state, action: PayloadAction<ISmartCoolerMasterProductResponse>) => {
      state.loadingProductById = false;
      state.smartCoolerProduct = action.payload;
      state.error = false;
    },
    // --- UPDATED ---
    fetchSmartCoolerProductByIdFailure: (state, action: PayloadAction<ApiErrorPayload | any>) => {
      state.loadingProductById = false;
      state.error = true;
      state.message = action.payload?.message || 'Failed to fetch product';
    },

    createSmartCoolerProduct: (state, _action: PayloadAction<ICreateSmartCoolerProductPayload>) => {
      state.loading = true;
      state.error = false;
      state.message = '';
    },
    createSmartCoolerProductSuccess: (state, action: PayloadAction<ISmartCoolerMasterProductResponse>) => {
      state.loading = false;
      state.smartCoolerProduct = action.payload;
      state.error = false;
      state.shouldRefetchSmartCoolerProducts = true;
    },
    // --- UPDATED ---
    createSmartCoolerProductFailure: (state, action: PayloadAction<ApiErrorPayload | any>) => {
      state.loading = false;
      state.error = true;
      state.message = action.payload?.message || 'Failed to create product';
    },

    updateSmartCoolerProduct: (state, _action: PayloadAction<{ id: string; payload: IUpdateSmartCoolerProductPayload }>) => {
      state.loading = true;
      state.error = false;
      state.message = '';
    },
    updateSmartCoolerProductSuccess: (state, action: PayloadAction<ISmartCoolerMasterProductResponse>) => {
      state.loading = false;
      state.smartCoolerProduct = action.payload;
      state.error = false;
      state.shouldRefetchSmartCoolerProducts = true;
    },
    // --- UPDATED ---
    updateSmartCoolerProductFailure: (state, action: PayloadAction<ApiErrorPayload | any>) => {
      state.loading = false;
      state.error = true;
      state.message = action.payload?.message || 'Failed to update product';
    },

    deleteSmartCoolerProduct: (state, _action: PayloadAction<{ smartCoolerProductId: string; transactionId: string }>) => {
      state.loading = true;
      state.error = false;
      state.message = '';
    },

deleteSmartCoolerProductSuccess: (state, action) => {
  state.loading = false;
  state.error = false;
  state.message = action.payload.message; 
  state.shouldRefetchSmartCoolerProducts = true;
},
deleteSmartCoolerProductFailure: (state, action) => {
  state.loading = false;
  state.error = true;
  state.message = action.payload; // Capture error message
},
    activateSmartCoolerProduct: (state, _action: PayloadAction<{ smartCoolerProductId: string; transactionId: string }>) => {
      state.loading = true;
      state.error = false;
      state.message = '';
    },
    activateSmartCoolerProductSuccess: (state, action: PayloadAction<any>) => {
      state.loading = false;
      state.error = false;
      state.shouldRefetchSmartCoolerProducts = true;
      state.message = action.payload?.message || 'Activated successfully';
    },
    // --- UPDATED ---
    activateSmartCoolerProductFailure: (state, action: PayloadAction<ApiErrorPayload | any>) => {
      state.loading = false;
      state.error = true;
      state.message = action.payload?.message || 'Failed to activate product';
    },

    clearSmartCoolerProduct: (state) => {
      state.smartCoolerProduct = initialState.smartCoolerProduct;
      state.loadingProductById = false;
      state.error = false;
      state.message = '';
    },
    setShouldRefetchSmartProducts: (state, action: PayloadAction<boolean>) => {
      state.shouldRefetchSmartCoolerProducts = action.payload;
    },
  },
});

export const {
  fetchSmartCoolerProducts,
  fetchSmartCoolerProductsSuccess,
  fetchSmartCoolerProductsFailure,
  fetchSmartCoolerProductById,
  fetchSmartCoolerProductByIdSuccess,
  fetchSmartCoolerProductByIdFailure,
  createSmartCoolerProduct,
  createSmartCoolerProductSuccess,
  createSmartCoolerProductFailure,
  updateSmartCoolerProduct,
  updateSmartCoolerProductSuccess,
  updateSmartCoolerProductFailure,
  deleteSmartCoolerProduct,
  deleteSmartCoolerProductSuccess,
  deleteSmartCoolerProductFailure,
  activateSmartCoolerProduct,
  activateSmartCoolerProductSuccess,
  activateSmartCoolerProductFailure,
  clearSmartCoolerProduct,
  setShouldRefetchSmartProducts
} = smartCoolerProductsSlice.actions;

export default smartCoolerProductsSlice.reducer;